import {
    ActionType, ApprovalScope, NetworkActionPrefix, NewsEntryEntryStatus, NewsEntryFilterPeriod, NewsEntrySourceCategory, NewsEntrySourceType, NewsEntryUserStatus, NewsfeedCategory,
    NewsfeedGroupMode, NewsfeedSortMode, NewsfeedUpdateType, PersistentNewsfeedKey, RankingScope, RequestStatus
} from '../utils/constants';
import defaults from '../api/defaults';
import { isInternalID, queryToEntity } from '../api/utils';
import { deepAssign } from '../utils';
import {
    combineReducers, networkEntityAddReducer,
    networkEntityDeleteReducer, networkEntityEditReducer, networkEntityLoadReducer, networkPaginationLoadReducer, optimisticNetworkEntityLoadReducer,
    paginationSlicePushReducer, pipeReducers, synchronizableEnumReducer, networkObjectLoadReducer, networkEntityAddAllReducer, networkEntityDeleteAllReducer
} from '../utils/reducerFactory';
import { LOCATION_CHANGE } from 'react-router-redux';
import { NewsEntryFilter } from 'for-straction-service-api';

function index(state, action, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

const keyVariables = {
        sourceIndex: (state, action) => index(state, action, action.sourceID),
        entryIndex: (state, action) => index(state, action, action.entryID),
        contributorIndex: (state, action) => index(state, action, action.contributorID),
        commentIndex: (state, action) => index(state, action, action.commentID)
    },

    // this actions happen in one newsfeed, but might also affect others in a quite complex manner (e. g. comment an entry in ALL scope -> become a contributor -> entry becomes visible in Tasks, linked topic feed, linked contributor feed etc)
    // for the target newsfeed actions are pretty straightforward, but for others it's easier to reload any data which came from server than to try to reimplement all of those consequences
    INTERFEED_ACTIONS = [
        ActionType.ADD_NEWSFEED_ENTRY_SUCCESS,
        ActionType.EDIT_NEWSFEED_ENTRY_SUCCESS,
        ActionType.ADD_NEWSFEED_COMMENT_SUCCESS,
        ActionType.ADD_NEWSFEED_CONTRIBUTOR_SUCCESS,
        ActionType.EDIT_NEWSFEED_CONTRIBUTOR_SUCCESS,
        ActionType.DELETE_NEWSFEED_CONTRIBUTOR_SUCCESS
    ],

    // this actions can affect news entries through a very complex chain of events (e. g. delete swot session -> delete swot topic -> watch unlinks from the topic -> source goes to another category -> ...)
    // it's easier to reload any data which came from server than to try to reimplement all of those consequences
    COMPLEXLY_RELATED_ACTIONS = [
        ActionType.DELETE_STAKEHOLDER_SUCCESS,
        ActionType.DELETE_SWOT_TOPIC_SUCCESS,
        ActionType.DELETE_SWOT_SESSION_SUCCESS,
        ActionType.DELETE_TREND_TOPIC_SUCCESS
    ],

    sourceNewsfeedKey = source => `${source.EntityType}_${source.ID}`,

    sources = function(state = defaults.NewsEntrySourceReportPaginationSlice, action) {
        switch (action.type) {

            case ActionType.SEARCH_NEWSFEED_SOURCES_SLICE_PENDING:
            case ActionType.SEARCH_NEWSFEED_SOURCES_SLICE_SUCCESS:
            case ActionType.SEARCH_NEWSFEED_SOURCES_SLICE_FAIL:
                return networkPaginationLoadReducer(NetworkActionPrefix.SEARCH_NEWSFEED_SOURCES_SLICE_PREFIX, defaults.NewsEntrySourceReportPaginationSlice)(
                    action.type === ActionType.SEARCH_NEWSFEED_SOURCES_SLICE_SUCCESS && !action.startID ?
                        defaults.NewsEntrySourceReportPaginationSlice :
                        state,
                    action
                );

            case ActionType.TOUCH_ALL_NEWSFEED_SOURCES_SUCCESS:
            case ActionType.TOUCH_NEWSFEED_SOURCE_SUCCESS:
                const sourceIDsToDelete = action.type === ActionType.TOUCH_ALL_NEWSFEED_SOURCES_SUCCESS ? action.sourceIDs : [action.sourceID],
                    sourcesToDelete = sourceIDsToDelete.map(id => state.Data.getByID(id)).filter(v => v);
                return deepAssign(state, {
                    Count: state.Count - sourcesToDelete.length,
                    TotalCount: state.TotalCount - sourcesToDelete.length,
                    CategoryCounts: state.CategoryCounts.map(c => ({ Count: c.Count - sourcesToDelete.filter(s => s.CategoryID === c.ID).length })),
                    Data: state.Data.filter(source => !sourceIDsToDelete.includes(source.ID))
                });

            case ActionType.EDIT_MARKET_FEED_BING_COGNITIVE_SUCCESS:
                const changed = state.Data.find(source => source.MarketFeedBingCognitiveID === action.marketFeedBingCognitive.ID);
                return changed ?
                    deepAssign(state, {
                        [state.Data.indexOf(changed)]: {
                            Name: action.marketFeedBingCognitive.Name
                        }
                    }) :
                    state;

            case ActionType.DELETE_NEWSFEED_SOURCE_PENDING:
                return deepAssign(state, {
                    Data: {
                        [state.Data.getIndexByID(action.sourceID)]: {
                            Custom: {
                                status: RequestStatus.PENDING
                            }
                        }
                    }
                });

                // modifications of watches affect related sources, but happen on other pages, so it's fine to force source reloading next time user wants to see sources
            case COMPLEXLY_RELATED_ACTIONS.find(actionType => action.type === actionType):
            case ActionType.ADD_MARKET_WATCH_SUCCESS:
            case ActionType.EDIT_MARKET_WATCH_SUCCESS:
            case ActionType.DELETE_MARKET_WATCH_SUCCESS:
            case ActionType.ADD_MARKET_RSS_WATCH_SETTING_SUCCESS:
            case ActionType.EDIT_MARKET_RSS_WATCH_SETTING_SUCCESS:
            case ActionType.DELETE_MARKET_RSS_WATCH_SETTING_SUCCESS:
            case ActionType.ADD_MARKET_FEED_BING_COGNITIVE_SUCCESS:
            case ActionType.DELETE_MARKET_FEED_BING_COGNITIVE_SUCCESS:
            case ActionType.DELETE_NEWSFEED_SOURCE_SUCCESS:
                // TODO invoke source list update without dropping data same as filters work
                // deletion of a source might affect other sources (new "sourceless" appears) or other sources' entries, we have to update all of them
                return defaults.NewsEntrySourceReportPaginationSlice;

            default:
                const oldState = state;
                state = deepAssign(state, {
                    Data: state.Data.map(source =>
                        action.newsfeedKey === sourceNewsfeedKey(source) ?
                            {
                                ...source,
                                Entries: (newsfeeds({ [sourceNewsfeedKey(source)]: { entries: source.Entries } }, action)[sourceNewsfeedKey(source)] || {
                                    entries: {
                                        ...source.Entries,
                                        Data: [],
                                        Count: 0
                                    }
                                }).entries
                            } :
                            source
                    )
                });
                const newState = state;

                if (oldState === newState) {
                    return state;
                }

                return deepAssign(state, {
                    RawEntriesCategoryCounts: state.RawEntriesCategoryCounts.map(c => ({
                        Count: c.Count + (
                            newState.Data
                                .filter(newSource => newSource.CategoryID === c.ID)
                                .reduce((count, newSource) => count + (newSource.Entries.EntryStatusCounts.getByID(NewsEntryEntryStatus.RAW) || { Count: 0 }).Count, 0) -
                        oldState.Data
                            .filter(oldSource => oldSource.CategoryID === c.ID)
                            .reduce((count, oldSource) => count + (oldSource.Entries.EntryStatusCounts.getByID(NewsEntryEntryStatus.RAW) || { Count: 0 }).Count, 0)
                        )
                    })),
                    RawEntriesStakeholderTypeCounts: state.RawEntriesStakeholderTypeCounts.map(c => ({
                        Count: c.Count + (
                            newState.Data
                                .filter(newSource => newSource.StakeholderTypeID === c.ID)
                                .reduce((count, newSource) => count + (newSource.Entries.EntryStatusCounts.getByID(NewsEntryEntryStatus.RAW) || { Count: 0 }).Count, 0) -
                        oldState.Data
                            .filter(oldSource => oldSource.StakeholderTypeID === c.ID)
                            .reduce((count, oldSource) => count + (oldSource.Entries.EntryStatusCounts.getByID(NewsEntryEntryStatus.RAW) || { Count: 0 }).Count, 0)
                        )
                    }))
                });
        }
    },


    loadNewsfeedEntry = networkEntityLoadReducer(NetworkActionPrefix.LOAD_NEWSFEED_ENTRY_PREFIX),

    optimisticLoadNewsfeedEntry = optimisticNetworkEntityLoadReducer(NetworkActionPrefix.LOAD_NEWSFEED_ENTRY_PREFIX, defaults.NewsEntryLightReport, (state, action) => action.entryID),

    entries = pipeReducers(
        (state, action) =>
            action.type === ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_PENDING ||
            action.type === ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_SUCCESS ||
            action.type === ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_FAIL ?
                networkPaginationLoadReducer(NetworkActionPrefix.SEARCH_NEWSFEED_ENTRIES_SLICE_PREFIX, defaults.NewsEntryLightReportPaginationSlice)(
                    deepAssign(state, {
                        ...action.type === ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_PENDING && action.updateType !== NewsfeedUpdateType.CHANGE_MODE ||
                            action.type !== ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_PENDING && action.updateType !== NewsfeedUpdateType.CHANGE_FILTER ?
                            state :
                            { Data: [], Count: 0, TotalCount: 0 },
                        Custom: { updateType: action.type === ActionType.SEARCH_NEWSFEED_ENTRIES_SLICE_PENDING ? action.updateType : null }
                    }), action
                ) :
                state,
        combineReducers({
            Data: combineReducers({
                '@entryIndex': pipeReducers(
                    (state, action) => action.optimistic ? optimisticLoadNewsfeedEntry(state, action) : loadNewsfeedEntry(state, action),
                    networkEntityAddReducer(NetworkActionPrefix.ADD_NEWSFEED_ENTRY_PREFIX, null, (state, action) => action.entry),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_NEWSFEED_ENTRY_PREFIX, null, (state, action) => action.entry),
                    combineReducers({
                        Contributors: pipeReducers(
                            paginationSlicePushReducer(ActionType.ADD_NEWSFEED_CONTRIBUTOR, []),
                            combineReducers({
                                Data: combineReducers({
                                    '@contributorIndex': pipeReducers(
                                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_NEWSFEED_CONTRIBUTOR_PREFIX),
                                        networkEntityAddReducer(NetworkActionPrefix.ADD_NEWSFEED_CONTRIBUTOR_PREFIX, null, (state, action) => action.contributor),
                                        networkEntityEditReducer(NetworkActionPrefix.EDIT_NEWSFEED_CONTRIBUTOR_PREFIX, null, (state, action) => action.contributor),
                                        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_NEWSFEED_CONTRIBUTOR_PREFIX)
                                    )
                                }, keyVariables)
                            })),
                        Comments: pipeReducers(
                            networkPaginationLoadReducer(NetworkActionPrefix.LOAD_NEWSFEED_COMMENTS_SLICE_PREFIX, []),
                            paginationSlicePushReducer(ActionType.LOAD_NEWSFEED_COMMENT_SUCCESS, []),
                            paginationSlicePushReducer(ActionType.ADD_NEWSFEED_COMMENT, []),
                            combineReducers({
                                Data: combineReducers({
                                    '@commentIndex': pipeReducers(
                                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_NEWSFEED_COMMENT_PREFIX, null),
                                        networkEntityAddReducer(NetworkActionPrefix.ADD_NEWSFEED_COMMENT_PREFIX, null, (state, action) => action.comment)
                                    )
                                }, keyVariables)
                            })),
                        InsightLinks: pipeReducers(
                            networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_NEWS_ENTRY_INSIGHT_LINKS_PREFIX, null, (state, action) => action.newsLinks),
                            networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_NEWS_ENTRY_INSIGHT_LINKS_PREFIX, null, (state, action) => action.newsLinkIDs)
                        )
                    }))
            }, keyVariables)
        })
    ),


    newsfeed = pipeReducers(
        (state, action) => action.type === ActionType.INITIALIZE_NEWSFEED ?
            newsfeed(undefined, { type: '@@redux/INIT' }) :
            state,
        combineReducers({
            scope: (state, action) =>
                action.type === LOCATION_CHANGE ?
                    +action.payload.query.scope || RankingScope.CONTRIBUTED :
                    state || RankingScope.CONTRIBUTED,
            filter: (state, action) =>
                action.type === LOCATION_CHANGE ?
                    deepAssign(state, queryToEntity(action.payload.query, NewsEntryFilter)) :
                    state || { ...defaults.NewsEntryFilter, Period: NewsEntryFilterPeriod.QUARTER },
            sortMode: (state, action) =>
                action.type === LOCATION_CHANGE ?
                    +action.payload.query.sortMode || NewsfeedSortMode.AGE :
                    state || NewsfeedSortMode.AGE,
            groupMode: (state, action) =>
                action.type === LOCATION_CHANGE ?
                    +action.payload.query.groupMode || NewsfeedGroupMode.IMPORTANCE :
                    state || NewsfeedGroupMode.IMPORTANCE,
            entries: (state = defaults.NewsEntryLightReportPaginationSlice, action) => {
                if (
                    isInternalID(action.entryID) &&
                action.type !== ActionType.ADD_NEWSFEED_ENTRY &&
                action.type !== ActionType.ADD_NEWSFEED_ENTRY_PENDING &&
                action.type !== ActionType.ADD_NEWSFEED_ENTRY_SUCCESS &&
                action.type !== ActionType.ADD_NEWSFEED_ENTRY_FAIL
                ) {
                    return state;
                }

                if (COMPLEXLY_RELATED_ACTIONS.includes(action.type)) {
                    return defaults.NewsEntryLightReportPaginationSlice;
                }

                const oldState = state;
                state = entries(state, action);
                const newState = state;

                if (oldState === newState) {
                    return state;
                }

                return deepAssign(state, {
                    Count: oldState.Count === newState.Count ?
                        state.Count + (newState.Data.length - oldState.Data.length) :
                        state.Count,
                    TotalCount: oldState.TotalCount === newState.TotalCount ?
                        state.TotalCount + (newState.Data.length - oldState.Data.length) :
                        state.TotalCount,
                    EntryStatusCounts: oldState.EntryStatusCounts === newState.EntryStatusCounts ?
                        state.EntryStatusCounts.map(c => ({
                            Count: c.Count + (
                                newState.Data.filter(newEntry => newEntry.EntryStatusID === c.ID).length -
                            oldState.Data.filter(oldEntry => oldEntry.EntryStatusID === c.ID).length
                            )
                        })) :
                        state.EntryStatusCounts,
                    // TODO update UserStatusID locally, currently numbers below won't be updated if a component changes a contributor without reloadAfterSync flag
                    UserStatusCounts: oldState.UserStatusCounts === newState.UserStatusCounts ?
                        state.UserStatusCounts.map(c => ({
                            Count: c.Count + (
                                newState.Data.filter(newEntry => newEntry.UserStatusID === c.ID).length -
                            oldState.Data.filter(oldEntry => oldEntry.UserStatusID === c.ID).length
                            )
                        })) :
                        state.UserStatusCounts,
                    SourceCategoryCounts: oldState.SourceCategoryCounts === newState.SourceCategoryCounts ?
                        state.SourceCategoryCounts.map(c => ({
                            Count: c.Count + (
                                newState.Data.filter(newEntry => newEntry.SourceCategoryID === c.ID).length -
                            oldState.Data.filter(oldEntry => oldEntry.SourceCategoryID === c.ID).length
                            )
                        })) :
                        state.SourceCategoryCounts,
                    SourceStakeholderTypeCounts: oldState.SourceStakeholderTypeCounts === newState.SourceStakeholderTypeCounts ?
                        state.SourceStakeholderTypeCounts.map(c => ({
                            Count: c.Count + (
                                newState.Data.filter(newEntry => newEntry.SourceStakeholderTypeID === c.ID).length -
                            oldState.Data.filter(oldEntry => oldEntry.SourceStakeholderTypeID === c.ID).length
                            )
                        })) :
                        state.SourceStakeholderTypeCounts
                });

            },
            newEntry: (state = defaults.NewsEntryLightReport, action) => {
                if (action.type === ActionType.ADD_NEWSFEED_ENTRY) {
                    return defaults.NewsEntryLightReport;
                }

                if (
                    !action.entryID ||
                !isInternalID(action.entryID) ||
                action.type === ActionType.ADD_NEWSFEED_ENTRY ||
                action.type === ActionType.ADD_NEWSFEED_ENTRY_PENDING ||
                action.type === ActionType.ADD_NEWSFEED_ENTRY_SUCCESS ||
                action.type === ActionType.ADD_NEWSFEED_ENTRY_FAIL
                ) {
                    return state;
                }

                if (action.type === ActionType.ADD_NEWSFEED_ENTRY) {
                    return defaults.NewsEntryLightReport;
                }

                return entries({ ...defaults.NewsEntryLightReportPaginationSlice, Data: [state] }, action).Data[0];
            }
        }),
        (state, _action) => deepAssign(state, { Custom: { expires: state.entries && state.entries.Custom.expires } }),
        (state, action) => action.type === ActionType.DROP_NEWSFEED ?
            Object.values(PersistentNewsfeedKey).includes(action.newsfeedKey) ?
                newsfeed(undefined, { type: '@@redux/INIT' }) :
                null :
            state
    ),

    newsfeedDictionary = combineReducers({ '@newsfeedKey': newsfeed }, { newsfeedKey: (state, action) => action.newsfeedKey }),

    newsfeeds = (state, action) =>
        action.type === LOCATION_CHANGE && action.payload.pathname.includes('/market_radar/news_ranking') ?
            newsfeedDictionary(state || Object.values(PersistentNewsfeedKey).reduce((r, k) => (r[k] = newsfeed(undefined, { type: '@@redux/INIT' }), r), {}), { ...action, newsfeedKey: PersistentNewsfeedKey.RANKING }) :
            action.type === LOCATION_CHANGE && action.payload.pathname === '/market_radar/market_feed/top' ?
                newsfeedDictionary(state || Object.values(PersistentNewsfeedKey).reduce((r, k) => (r[k] = newsfeed(undefined, { type: '@@redux/INIT' }), r), {}), { ...action, newsfeedKey: PersistentNewsfeedKey.TOP_NEWS }) :
                COMPLEXLY_RELATED_ACTIONS.includes(action.type) ?
                    deepAssign(state, Object.keys(state).reduce((r, k) => (r[k] = newsfeed(state[k], action), r), {})) :
                    INTERFEED_ACTIONS.includes(action.type) ?
                        newsfeedDictionary(
                            state ?
                                Object.keys(state).reduce((r, k) => (r[k] = k === action.newsfeedKey ? state[k] : newsfeed(undefined, { type: '@@redux/INIT' }), r), {}) :
                                Object.values(PersistentNewsfeedKey).reduce((r, k) => (r[k] = newsfeed(undefined, { type: '@@redux/INIT' }), r), {}),
                            action
                        ) :
                        newsfeedDictionary(Object.values(PersistentNewsfeedKey).reduce((r, k) => (r[k] = r[k] || newsfeed(undefined, { type: '@@redux/INIT' }), r), state || {}), action),


    news = pipeReducers(
        combineReducers({
            categories: synchronizableEnumReducer(NetworkActionPrefix.LOAD_NEWSFEED_CATEGORIES_PREFIX, defaults.NetworkArray, NewsfeedCategory, 'NewsEntryCategory'),
            entryStatuses: synchronizableEnumReducer(NetworkActionPrefix.LOAD_NEWSFEED_ENTRY_STATUSES_PREFIX, defaults.NetworkArray, NewsEntryEntryStatus, 'NewsEntryEntryStatus'),
            userStatuses: synchronizableEnumReducer(NetworkActionPrefix.LOAD_NEWSFEED_USER_STATUSES_PREFIX, defaults.NetworkArray, NewsEntryUserStatus, 'NewsEntryUserStatus'),
            sourceTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_NEWSFEED_SOURCE_TYPES_PREFIX, defaults.NetworkArray, NewsEntrySourceType, 'NewsEntrySourceType'),
            sourceCategories: pipeReducers(
                (state, action) => action.type === ActionType.EDIT_TRACKING_LEVEL_SUCCESS ? defaults.NetworkArray : state,
                synchronizableEnumReducer(NetworkActionPrefix.LOAD_NEWSFEED_SOURCE_CATEGORIES_PREFIX, defaults.NetworkArray, NewsEntrySourceCategory, 'NewsEntrySourceCategory')
            ),
            sourcesScope: (state, action) =>
                action.type === LOCATION_CHANGE && (action.payload.pathname === '/market_radar/market_feed/newsroom' || action.payload.pathname.startsWith('/agenda360/newsroom')) ?
                    +action.payload.query.scope || ApprovalScope.USER :
                    state || ApprovalScope.USER,
            sourcesFilter: (state, action) =>
                action.type === LOCATION_CHANGE && (action.payload.pathname === '/market_radar/market_feed/newsroom' || action.payload.pathname.startsWith('/agenda360/newsroom')) ?
                    deepAssign(state, queryToEntity(action.payload.query, NewsEntryFilter)) :
                    state || defaults.NewsEntryFilter,
            sourcesMapScope: (state, action) =>
                action.type === LOCATION_CHANGE && action.payload.pathname === '/market_radar/market_feed/sources/map' ?
                    action.payload.query.sourcesMapScope || ApprovalScope.ALL :
                    state || ApprovalScope.ALL,
            sourcesMapFilter: (state, action) =>
                action.type === LOCATION_CHANGE && action.payload.pathname === '/market_radar/market_feed/sources/map' ?
                    deepAssign(state, queryToEntity(action.payload.query, NewsEntryFilter)) :
                    state || defaults.NewsEntryFilter
        }),
        (state, action) => {
        // source-related newfeeds are actually stored inside corresponding sources, but for consistency we copy them to common newsfeeds collection
            const oldSources = state.sources,
                oldNewsfeeds = state.newsfeeds,
                oldNonSourcesNewsfeeds = oldNewsfeeds ? { ...oldNewsfeeds } : {};

            if (oldSources) {
                oldSources.Data.forEach((source) => {
                    delete oldNonSourcesNewsfeeds[sourceNewsfeedKey(source)];
                });
            }

            Object.values(PersistentNewsfeedKey).forEach(k => oldNonSourcesNewsfeeds[k] = oldNonSourcesNewsfeeds[k] || newsfeed(undefined, { type: '@@redux/INIT' }));

            const newSources = sources(oldSources, action),
                newNonSourcesNewsfeeds = newsfeeds(oldNonSourcesNewsfeeds, action),
                newNewsfeeds = { ...newNonSourcesNewsfeeds };

            if (newSources) {
                newSources.Data.forEach((source) => {
                    newNewsfeeds[sourceNewsfeedKey(source)] = { entries: source.Entries };
                });
            }

            for (const key in state.newsfeeds) {
                newNewsfeeds[key] = newNewsfeeds[key] || undefined;
            }

            return deepAssign(state, { sources: newSources, newsfeeds: newNewsfeeds });
        }
    );

export default news;
